import React, { useMemo } from 'react';
import { View, Text, StyleSheet, useWindowDimensions } from 'react-native';
import PostGridDisplay from './PostGridDisplay';
import { getResponsiveValue } from '../styles/breakpoints';

/**
 * Compact card for a saved community post in MediaManager.
 * - `previewOnly`: grid only (cream tile); use with a separate filename row below, like other library files.
 * - default: CommunityList-style card with header text + grid.
 * Double-tap is handled by parent (ItemWrapper + handleItemPress) to open post modal.
 */
export function CommunityPostCardFile({ post, name: fileName, isSelected, previewOnly = false }) {
  const { width: windowWidth } = useWindowDimensions();
  const previewColWidth = useMemo(
    () =>
      previewOnly
        ? getResponsiveValue({
            mobile: 20,
            tablet: 24,
            laptop: 28,
            desktop: 28,
          })
        : getResponsiveValue({
            mobile: 40,
            tablet: 44,
            laptop: 50,
            desktop: 50,
          }),
    [previewOnly, windowWidth]
  );
  const previewRowHeight = useMemo(
    () =>
      previewOnly
        ? getResponsiveValue({
            mobile: 12,
            tablet: 15,
            laptop: 18,
            desktop: 18,
          })
        : getResponsiveValue({
            mobile: 22,
            tablet: 25,
            laptop: 28,
            desktop: 28,
          }),
    [previewOnly, windowWidth]
  );
  const previewCardPadding = useMemo(
    () =>
      getResponsiveValue({
        mobile: 4,
        tablet: 6,
        laptop: 6,
        desktop: 6,
      }),
    [windowWidth]
  );

  const postItems = Array.isArray(post?.items) ? post.items : [];
  const title =
    (typeof fileName === 'string' ? fileName.trim() : '') ||
    (typeof post?.postTitle === 'string' ? post.postTitle.trim() : '');
  const content = (post?.postContent || '').trim();
  const loc = (post?.location || '').trim();
  const showContent = Boolean(content && content !== title);
  const hasHeader = Boolean(title || showContent || loc);

  const gridBlock = postItems.length > 0 ? (
    <PostGridDisplay
      items={postItems}
      colWidth={previewColWidth}
      rowHeight={previewRowHeight}
      style={previewOnly ? styles.gridPreview : styles.grid}
      compactMode
      suppressEmptyWidgetHints
    />
  ) : (
    <View style={previewOnly ? styles.gridPlaceholderPreview : styles.gridPlaceholder}>
      <Text style={styles.gridPlaceholderText}>No media</Text>
    </View>
  );

  if (previewOnly) {
    return (
      <View style={styles.containerPreview}>
        <View style={[styles.cardPreview, { padding: previewCardPadding }]}>
          {gridBlock}
        </View>
      </View>
    );
  }

  return (
    <View style={[styles.container]}>
      <View style={styles.card}>
        <View style={styles.header}>
          <View style={styles.headerInputs}>
            {title ? (
              <Text style={styles.postTitle} numberOfLines={2}>{title}</Text>
            ) : null}
            {showContent ? (
              <Text style={styles.postInput} numberOfLines={2}>{content}</Text>
            ) : null}
            {loc ? (
              <Text style={styles.locationInput} numberOfLines={1}>{loc}</Text>
            ) : null}
            {!hasHeader ? (
              <Text style={styles.postInput} numberOfLines={1}>Post</Text>
            ) : null}
          </View>
        </View>
        {gridBlock}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  containerPreview: {
    width: '100%',
    height: '100%',
    flex: 1,
    borderRadius: 12,
    overflow: 'hidden',
  },
  cardPreview: {
    flex: 1,
    width: '100%',
    backgroundColor: '#feffe4',
    borderRadius: 12,
    padding: 6,
    justifyContent: 'center',
    alignItems: 'center',
  },
  gridPreview: {
    maxWidth: '100%',
    borderRadius: 6,
    overflow: 'hidden',
  },
  gridPlaceholderPreview: {
    minHeight: 48,
    borderRadius: 6,
    backgroundColor: 'rgba(0,0,0,0.06)',
    alignItems: 'center',
    justifyContent: 'center',
    alignSelf: 'stretch',
    paddingVertical: 12,
  },
  container: {
    width: '100%',
    minHeight: 120,
    borderRadius: 12,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  containerSelected: {
    borderWidth: 2,
    borderColor: '#e67e22',
  },
  card: {
    flex: 1,
    backgroundColor: '#feffe4',
    borderRadius: 12,
    padding: 12,
    width: '100%',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 8,
    width: '100%',
  },
  headerInputs: {
    flex: 1,
  },
  postTitle: {
    fontFamily: 'Computer Says No',
    fontSize: 16,
    fontWeight: '600',
    color: '#000',
    paddingVertical: 2,
  },
  postInput: {
    fontFamily: 'Computer Says No',
    fontSize: 16,
    color: '#000',
    paddingVertical: 2,
  },
  locationInput: {
    fontFamily: 'Computer Says No',
    fontSize: 13,
    color: '#333',
    paddingVertical: 2,
  },
  grid: {
    minHeight: 56,
    borderRadius: 6,
    overflow: 'hidden',
    alignSelf: 'flex-start',
  },
  gridPlaceholder: {
    minHeight: 56,
    borderRadius: 6,
    backgroundColor: 'rgba(0,0,0,0.06)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  gridPlaceholderText: {
    fontFamily: 'VastagoGrotesk',
    fontSize: 11,
    color: '#888',
  },
});
